import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, getDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getLiveCollection } from '../../utils/liveData';
import { useAuth, ROLES, PERMISSIONS } from '../../hooks/useAuth';
import OrderDetails from '../orders/OrderDetails';
import CustomerCard from './CustomerCard';
import { ORDER_STATUS, ORDER_STATUS_LABELS, PAGINATION_OPTIONS } from '../../constants';
import { formatDate, formatDateForInput } from '../../utils';
import { StatusChip, PaymentChip, STATUS_TONES, displayPhone, getInitials } from './CustomerBadges';
import {
  PlusIcon, XIcon, SearchIcon, ListIcon, GridIcon, PencilIcon, EyeIcon, CopyIcon, TrashIcon,
  ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon, UsersIcon
} from '../common/Icons';
import './CustomerList.css';
import './CustomerListModern.css';

const CustomerList = ({ onCustomerChange, userRole, userId, startAdding = false }) => {
  const { user, permissions, isMontajci, isYazilimci } = useAuth();
  
  // Telefon numarası formatlama (5XX XXX XX XX)
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  // TC Kimlik Numarası Doğrulama Algoritması
  const isTCValid = (tc) => {
    if (!tc) return true;
    const digits = tc.toString().replace(/\D/g, '').split('').map(Number);
    
    // Vergi numarası 10 haneli olur, TC 11 haneli. 10 hane ise TC doğrulaması yapma.
    if (digits.length === 10) return true; 
    
    if (digits.length !== 11) return false;
    if (digits[0] === 0) return false;
    
    const d = digits;
    const oddSum = d[0] + d[2] + d[4] + d[6] + d[8];
    const evenSum = d[1] + d[3] + d[5] + d[7];
    
    // 10. hane kontrolü
    const d10 = (oddSum * 7 - evenSum) % 10;
    if ((d10 < 0 ? d10 + 10 : d10) !== d[9]) return false;
    
    // 11. hane kontrolü
    if ((oddSum + evenSum + d[9]) % 10 !== d[10]) return false;
    
    return true;
  };
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone1: '',
    phone2: '',
    email: '',
    address: '',
    notes: '',
    inspectionDate: '',
    inspectionTime: '',
    orderDate: '',
    deliveryDate: '',
    deliveryTime: '',
    orderStatus: ORDER_STATUS.NEW_ORDER,
    paidAmount: '',
    isPaymentCompleted: false,
    remainingAmount: 0,
    tcNo: ''
  });
  const [isAdding, setIsAdding] = useState(startAdding); // Anasayfadaki "Yeni müşteri" ile form açık gelir
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isCustomerInfoCollapsed, setIsCustomerInfoCollapsed] = useState(false); // Müşteri bilgileri için
  const [lastSubmitTime, setLastSubmitTime] = useState(0); // Çifte submit koruması için
  const isSubmittingRef = useRef(false); // Immediate blocking için

  // Aşamalı işlem için step state'i
  const [currentStep, setCurrentStep] = useState('customer'); // 'customer' veya 'order'

  // Filtreleme ve arama state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState('table'); // 'table' veya 'card'
  const [remainingAmounts, setRemainingAmounts] = useState({}); // Müşteri ID'lerine göre kalan ödeme

  // Toast notification gösterme fonksiyonu
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Kalan ödeme hesaplama fonksiyonu
  const calculateRemainingAmount = useCallback(async (customer) => {
    try {
      // Ödeme tamamlandıysa kalan 0
      if (customer.isPaymentCompleted) {
        return 0;
      }

      // Ödenen tutar
      const paid = parseFloat(customer.paidAmount) || 0;

      // Siparişleri paylaşılan canlı listeden al (müşteri başına sorgu = ek okuma)
      const ordersSnapshot = await getLiveCollection('orders');
      const orders = ordersSnapshot.docs.map(doc => doc.data()).filter(order => order.customerId === customer.id);

      if (orders.length === 0) {
        return -1; // Sipariş yok anlamına gelir
      }

      // Toplam tutar
      const totalAmount = orders.reduce((sum, order) => {
        return sum + parseFloat(order.totalPrice || 0);
      }, 0);

      // İndirimli tutar
      const discountedTotalAmount = orders.reduce((sum, order) => {
        if (order.discountedPrice && parseFloat(order.discount) > 0) {
          return sum + parseFloat(order.discountedPrice || 0);
        } else {
          return sum + parseFloat(order.totalPrice || 0);
        }
      }, 0);

      // Yeni Fiyatlandırma Hiyerarşisi
      let grandTotal = 0;

      // 1. Manuel Genel Toplam (En yüksek öncelik)
      if (customer.manualGrandTotal !== undefined && customer.manualGrandTotal !== null && customer.manualGrandTotal !== '') {
        grandTotal = parseFloat(customer.manualGrandTotal);
      } else {
        // 2. Vadeli Yüzde
        const vadeliYuzde = parseFloat(customer.vadeliYuzde) || 0;
        const genelIskontoYuzde = parseFloat(customer.genelIskontoYuzde) || 0;

        if (vadeliYuzde > 0) {
          grandTotal = totalAmount + (totalAmount * vadeliYuzde / 100);
        } else if (genelIskontoYuzde > 0) {
          // 3. Genel İskonto
          grandTotal = totalAmount - (totalAmount * genelIskontoYuzde / 100);
        } else if (discountedTotalAmount !== totalAmount && discountedTotalAmount > 0) {
          // 4. Tekil İskontolu Tutar
          grandTotal = discountedTotalAmount;
        } else {
          // 5. Normal Toplam
          grandTotal = totalAmount;
        }
      }

      const remaining = grandTotal - paid;
      return remaining < 0 ? 0 : remaining;
    } catch (error) {
      console.error('Kalan ödeme hesaplanırken hata:', error);
      return 0;
    }
  }, []);

  // Müşterileri Firebase'den çek
  const fetchCustomers = useCallback(async () => {
    try {
      setIsLoading(true);

      let customersQuery;

      // Yazılımcı rolü: sadece kendi oluşturduğu müşterileri gör
      if (isYazilimci && userId) {
        customersQuery = query(
          collection(db, 'customers'),
          where('createdBy', '==', userId)
        );
      }

      // Admin, Satışcı: tüm müşterileri paylaşılan canlı listeden al (her yenilemede yeniden okunmaz)
      const querySnapshot = customersQuery ? await getDocs(customersQuery) : await getLiveCollection('customers');
      let customersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Eğer data içinde 'id' field'ı varsa temizle, document ID'yi kullan
        const { id: dataId, ...cleanData } = data;

        if (dataId && dataId !== doc.id) {
          console.warn('⚠️ Veri tutarsızlığı tespit edildi:', {
            documentId: doc.id,
            dataId: dataId,
            customerName: data.name
          });
        }

        return {
          id: doc.id, // Her zaman Firebase document ID'si kullan
          ...cleanData
        };
      });

      // Montajcı rolü: sadece sipariş durumu "Montaj" olan müşterileri gör
      if (isMontajci) {
        customersData = customersData.filter(c => c.orderStatus === ORDER_STATUS.MONTAJ);
      }

      // Client-side'da tarihe göre sırala (en yeni en üstte)
      customersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
        return dateB.getTime() - dateA.getTime(); // Descending order
      });

      setCustomers(customersData);

      // Kalan ödemeleri hesapla - önce müşteri kaydındaki remainingAmount'u kullan, yoksa hesapla
      const amounts = {};
      for (const customer of customersData) {
        // Müşteri kaydında zaten remainingAmount varsa onu kullan
        // Not: -1 değeri "sipariş yok" anlamına gelir, bu değeri de kabul et
        if (customer.remainingAmount !== undefined && customer.remainingAmount !== null && customer.remainingAmount !== 0) {
          amounts[customer.id] = parseFloat(customer.remainingAmount);
        } else if (customer.isPaymentCompleted) {
          // Ödeme tamamlandıysa 0
          amounts[customer.id] = 0;
        } else {
          // Yoksa veya 0 ise undefined olarak bırak, sonra lazy olarak hesaplanacak (performans için)
          // 0 değeri hem "ödeme alındı" hem "sipariş yok" olabilir, bu yüzden tekrar hesaplanmalı
          // undefined olursa "..." gösterilecek, hesaplanana kadar
          amounts[customer.id] = undefined;
        }
      }
      setRemainingAmounts(amounts);

      // Arka planda sadece remainingAmount olmayan veya 0 olan müşteriler için hesapla (lazy loading)
      // Bu işlemi async olarak yap, UI'ı bloklamasın
      // Hesaplanan değerleri Firebase'e kaydet ki bir sonraki yüklemede hızlı olsun
      // Not: remainingAmount 0 olanlar için de hesaplama yap çünkü "sipariş yok" (-1) olabilir
      if (!isMontajci) setTimeout(async () => {
        const missingAmounts = {};
        const updatesToSave = [];

        for (const customer of customersData) {
          // remainingAmount undefined, null veya 0 ise hesapla
          // 0 değeri hem "ödeme alındı" hem "sipariş yok" olabilir
          const needsCalculation = customer.remainingAmount === undefined ||
            customer.remainingAmount === null ||
            customer.remainingAmount === 0;

          if (needsCalculation) {
            if (!customer.isPaymentCompleted) {
              const remaining = await calculateRemainingAmount(customer);
              missingAmounts[customer.id] = remaining;

              // Değer değişmediyse yazma (her yazma, dinleyen tüm cihazlarda yeniden okuma demektir)
              if (remaining === customer.remainingAmount) continue;

              // Firebase'e kaydet (arka planda, hata olursa sessizce devam et)
              updatesToSave.push(
                updateDoc(doc(db, 'customers', customer.id), {
                  remainingAmount: remaining,
                  updatedAt: new Date()
                }).catch(err => {
                  console.warn(`Müşteri ${customer.id} için remainingAmount kaydedilemedi:`, err);
                })
              );
            } else {
              // Ödeme tamamlandıysa 0 olarak kaydet
              missingAmounts[customer.id] = 0;
              if (customer.remainingAmount === 0) continue;
              updatesToSave.push(
                updateDoc(doc(db, 'customers', customer.id), {
                  remainingAmount: 0,
                  updatedAt: new Date()
                }).catch(err => {
                  console.warn(`Müşteri ${customer.id} için remainingAmount kaydedilemedi:`, err);
                })
              );
            }
          }
        }

        // UI'ı güncelle
        if (Object.keys(missingAmounts).length > 0) {
          setRemainingAmounts(prev => ({ ...prev, ...missingAmounts }));
        }

        // Firebase'e kaydet (tüm güncellemeleri paralel yap)
        if (updatesToSave.length > 0) {
          Promise.all(updatesToSave).then(() => {
            console.log(`✅ ${updatesToSave.length} müşteri için remainingAmount Firebase'e kaydedildi`);
          }).catch(err => {
            console.warn('Bazı remainingAmount güncellemeleri başarısız oldu:', err);
          });
        }
      }, 100);

      // Dashboard istatistiklerini güncelle
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
      showNotification('Müşteriler yüklenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [onCustomerChange, calculateRemainingAmount, isYazilimci, isMontajci, userId]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Müşterileri filtrele
  useEffect(() => {
    let filtered = customers;

    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(customer =>
        customer.name?.toLocaleLowerCase('tr-TR').includes(searchLower) ||
        customer.phone1?.includes(searchTerm) ||
        customer.address?.toLocaleLowerCase('tr-TR').includes(searchLower)
      );
    }

    // Durum filtresi
    if (statusFilter) {
      filtered = filtered.filter(customer => customer.orderStatus === statusFilter);
    }

    setFilteredCustomers(filtered);
    setCurrentPage(1); // Filtre değiştiğinde ilk sayfaya dön
  }, [customers, searchTerm, statusFilter]);

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  // Mobilde tablo yerine her zaman kart görünümü
  const [isMobile, setIsMobile] = useState(() => window.matchMedia?.('(max-width: 768px)').matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.('(max-width: 768px)');
    if (!mq) return undefined;
    const update = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update));
  }, []);
  const effectiveView = isMobile ? 'card' : viewMode;

  // Durum filtresi sayıları (aramaya göre süzülmüş liste üzerinden)
  const statusCounts = useMemo(() => {
    const term = searchTerm.toLocaleLowerCase('tr-TR');
    const counts = { all: 0 };
    customers.forEach(c => {
      if (term && !(
        c.name?.toLocaleLowerCase('tr-TR').includes(term) ||
        c.phone1?.includes(searchTerm) ||
        c.address?.toLocaleLowerCase('tr-TR').includes(term)
      )) return;
      const status = c.orderStatus || ORDER_STATUS.NEW_ORDER;
      counts.all += 1;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [customers, searchTerm]);

  const pendingPaymentCount = useMemo(
    () => customers.filter(c => !c.isPaymentCompleted && remainingAmounts[c.id] > 0).length,
    [customers, remainingAmounts]
  );

  // Sayfa değiştirme
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa başına öğe sayısını değiştirme
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Yeni müşteri ekle
  const handleAddCustomer = async (e) => {
    e.preventDefault();

    // IMMEDIATE BLOCKING - Hiçbir koşulda geçilmez
    if (isSubmittingRef.current) {
      console.log('🚫 IMMEDIATE BLOCK - Already submitting');
      return;
    }

    // Çifte submit koruması - 2 saniye içinde ikinci submit'i engelle
    const now = Date.now();
    if (now - lastSubmitTime < 2000) {
      console.log('⚠️ Çifte submit engellendi:', {
        timeDiff: now - lastSubmitTime,
        lastSubmit: new Date(lastSubmitTime).toLocaleTimeString()
      });
      showNotification('Lütfen bekleyin, işlem devam ediyor...', 'warning');
      return;
    }

    // Zaten işlem devam ediyorsa engelleyelin
    if (isLoading) {
      console.log('⚠️ Loading state aktif, submit engellendi');
      return;
    }

    if (!newCustomer.name || !newCustomer.phone1) {
      showNotification('Ad Soyad ve Telefon 1 alanları zorunludur!', 'warning');
      return;
    }

    // TC No Doğrulaması
    if (newCustomer.tcNo && !isTCValid(newCustomer.tcNo)) {
      showNotification('Lütfen geçerli bir TC Kimlik Numarası giriniz!', 'error');
      return;
    }

    try {
      // İşlem başladığını hemen işaretle
      isSubmittingRef.current = true;
      setLastSubmitTime(now);
      setIsLoading(true);

      console.log('📝 Müşteri ekleme başladı:', new Date().toLocaleTimeString());

      // ID field'ını temizle (Firebase kendi document ID'sini verecek)
      const { id, ...customerDataWithoutId } = newCustomer;

      const customerData = {
        ...customerDataWithoutId,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null // Hangi kullanıcı oluşturduğu
      };

      console.log('🧹 ID temizlendi, kayıt yapılıyor:', {
        removedId: id,
        hasNameField: !!customerData.name
      });

      // Müşteriyi Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'customers'), customerData);

      console.log('✅ Müşteri Firebase\'e kaydedildi:', docRef.id);

      // Kaydedilen müşterinin ID'sini al
      const savedCustomer = { ...newCustomer, id: docRef.id };

      // Sipariş aşamasına geç
      setCurrentStep('order');
      setNewCustomer(savedCustomer);

      // Müşteri listesini yenile
      await fetchCustomers();

      // Ekranı yukarı scroll et
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

      showNotification('Müşteri başarıyla kaydedildi! Şimdi sipariş detaylarını ekleyebilirsiniz.', 'success');
    } catch (error) {
      console.error('❌ Müşteri eklenirken hata:', error);
      showNotification('Müşteri eklenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      // İşlem bitti, blokları kaldır
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Müşteri ekleme işlemini tamamla
  const handleCompleteCustomer = async () => {
    try {
      // Müşteri bilgilerini güncelle (sipariş durumu dahil)
      if (newCustomer.id && !newCustomer.id.startsWith('temp_')) {
        console.log('🔄 Müşteri güncelleniyor:', {
          customerId: newCustomer.id,
          orderStatus: newCustomer.orderStatus
        });

        // Direkt Firebase'den müşteriyi kontrol et (state timing sorunu için)
        try {
          const customersSnapshot = isYazilimci ? null : await getLiveCollection('customers');
          const customerExists = customersSnapshot
            ? customersSnapshot.docs.some(d => d.id === newCustomer.id)
            : (await getDoc(doc(db, 'customers', newCustomer.id))).exists();

          if (!customerExists) {
            console.error('❌ Müşteri Firebase\'de bulunamadı:', newCustomer.id);
            showNotification('Müşteri bulunamadı. Sayfa yenilenecek.', 'error');
            await fetchCustomers();
            setIsAdding(false);
            setCurrentStep('customer');
            return;
          }

          console.log('✅ Müşteri Firebase\'de mevcut:', newCustomer.id);
        } catch (checkError) {
          console.error('❌ Müşteri kontrol hatası:', checkError);
          showNotification('Müşteri kontrolü yapılamadı: ' + checkError.message, 'error');
          return;
        }

        // Ödeme bilgilerini de kaydet
        const paidAmountValue = (newCustomer.paidAmount && newCustomer.paidAmount.toString().trim() !== '')
          ? parseFloat(newCustomer.paidAmount)
          : 0;

        // Siparişleri kontrol et - sipariş yoksa remainingAmount -1 olmalı
        const ordersSnapshot = await getLiveCollection('orders');
        const hasOrders = ordersSnapshot.docs.some(d => d.data().customerId === newCustomer.id);

        // remainingAmount hesapla: sipariş yoksa -1, varsa newCustomer'dan al
        let remainingAmountValue;
        if (!hasOrders) {
          remainingAmountValue = -1; // Sipariş yok
        } else if (newCustomer.isPaymentCompleted) {
          remainingAmountValue = 0; // Ödeme tamamlandı
        } else {
          remainingAmountValue = newCustomer.remainingAmount || 0;
        }

        await updateDoc(doc(db, 'customers', newCustomer.id), {
          orderStatus: newCustomer.orderStatus,
          vadeliYuzde: newCustomer.vadeliYuzde || '',
          genelIskontoYuzde: newCustomer.genelIskontoYuzde || '',
          paidAmount: paidAmountValue,
          isPaymentCompleted: newCustomer.isPaymentCompleted || false,
          remainingAmount: remainingAmountValue,
          updatedAt: new Date()
        });

        console.log('✅ Müşteri güncellendi:', newCustomer.id);
      } else {
        console.warn('⚠️ Geçersiz customer ID, güncelleme atlanıyor:', newCustomer.id);
      }

      // Formu temizle
      setNewCustomer({
        name: '',
        phone1: '',
        phone2: '',
        email: '',
        address: '',
        notes: '',
        inspectionDate: '',
        inspectionTime: '',
        orderDate: '',
        deliveryDate: '',
        deliveryTime: '',
        orderStatus: ORDER_STATUS.NEW_ORDER,
        vadeliYuzde: '',
        genelIskontoYuzde: '',
        paidAmount: '',
        isPaymentCompleted: false,
        remainingAmount: 0,
        tcNo: ''
      });
      setIsAdding(false);
      setCurrentStep('customer');

      // Müşteri listesini yenile
      await fetchCustomers();

      showNotification('Müşteri ve sipariş bilgileri başarıyla kaydedildi!', 'success');
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      showNotification('Müşteri güncellenirken bir hata oluştu: ' + error.message, 'error');
    }
  };

  // Sipariş durumu değişikliği için callback
  const handleNewCustomerOrderStatusChange = useCallback((status) => {
    setNewCustomer(prev => ({ ...prev, orderStatus: status }));
  }, []);

  // Vadeli yüzde değişikliği için callback (yeni müşteri)
  const handleNewCustomerVadeliYuzdeChange = useCallback((vadeliYuzde) => {
    setNewCustomer(prev => ({ ...prev, vadeliYuzde: vadeliYuzde }));
  }, []);

  // Genel iskonto yüzde değişikliği için callback (yeni müşteri)
  const handleNewCustomerGenelIskontoYuzdeChange = useCallback((genelIskontoYuzde) => {
    setNewCustomer(prev => ({ ...prev, genelIskontoYuzde: genelIskontoYuzde }));
  }, []);

  // Düzenleme modunda fiyat (vade/iskonto) kaydetme başarılı olduğunda
  const handleEditCustomerVadeliSaveSuccess = useCallback(async (pricingData) => {
    const { vadeliYuzde, genelIskontoYuzde, manualGrandTotal } = pricingData;
    
    // editingCustomer state'ini güncelle (anlık görüntü için)
    if (editingCustomer) {
      const customerId = editingCustomer.id;
      const updatedCustomer = { 
        ...editingCustomer, 
        vadeliYuzde, 
        genelIskontoYuzde, 
        manualGrandTotal 
      };
      setEditingCustomer(updatedCustomer);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? updatedCustomer
            : customer
        )
      );

      // Kalan ödeme bilgisini yenile
      const newRemaining = await calculateRemainingAmount(updatedCustomer);
      setRemainingAmounts(prev => ({
        ...prev,
        [customerId]: newRemaining
      }));
    }

    showNotification('Fiyat bilgileri başarıyla güncellendi!', 'success');
  }, [editingCustomer, calculateRemainingAmount]);

  // Ödeme bilgisi güncellendiğinde sadece ilgili müşteriyi güncelle (tüm listeyi yenilemeden)
  const handleCustomerPaymentUpdate = useCallback((updatedCustomerInfo) => {
    // editingCustomer state'ini güncelle
    if (editingCustomer && editingCustomer.id === updatedCustomerInfo.id) {
      setEditingCustomer(updatedCustomerInfo);
    }

    // Müşteri listesindeki ilgili müşteriyi güncelle (tüm listeyi yenilemeden)
    setCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === updatedCustomerInfo.id
          ? { ...customer, ...updatedCustomerInfo }
          : customer
      )
    );

    // Kalan ödeme state'ini güncelle
    setRemainingAmounts(prev => ({
      ...prev,
      [updatedCustomerInfo.id]: updatedCustomerInfo.remainingAmount || 0
    }));

    showNotification('Ödeme bilgisi başarıyla güncellendi!', 'success');
  }, [editingCustomer]);

  // Sipariş durumu değiştiğinde müşteri listesini güncelle
  const handleOrderStatusChange = async (customerId, newStatus) => {
    // Customer ID geçerliliğini kontrol et
    if (!customerId || customerId.startsWith('temp_')) {
      showNotification('Geçersiz müşteri ID\'si. Sayfayı yenileyip tekrar deneyin.', 'error');
      return;
    }

    try {
      // Önce müşterinin var olup olmadığını kontrol et
      const customerExists = customers.find(c => c.id === customerId);

      if (!customerExists) {
        showNotification('Müşteri bulunamadı. Sayfa yenilenecek.', 'error');
        await fetchCustomers(); // Listeyi yenile
        return;
      }

      await updateDoc(doc(db, 'customers', customerId), {
        orderStatus: newStatus,
        updatedAt: new Date()
      });

      // editingCustomer state'ini güncelle
      if (editingCustomer && editingCustomer.id === customerId) {
        setEditingCustomer({
          ...editingCustomer,
          orderStatus: newStatus
        });
      }

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? { ...customer, orderStatus: newStatus }
            : customer
        )
      );

      showNotification('Sipariş durumu başarıyla güncellendi!', 'success');
    } catch (error) {
      console.error('Sipariş durumu güncellenirken hata:', error);

      // Eğer döküman bulunamadı hatası ise, UI'ı temizle
      if (error.code === 'not-found') {
        // Edit modunu kapat
        if (editingCustomer && editingCustomer.id === customerId) {
          setEditingCustomer(null);
          document.body.classList.remove('modal-open');
        }

        // Listeyi yenile
        await fetchCustomers();

        showNotification('Bu müşteri artık mevcut değil. Liste güncellendi.', 'warning');
      } else {
        showNotification('Sipariş durumu güncellenirken bir hata oluştu: ' + error.message, 'error');

        // Hata durumunda listeyi yenile
        await fetchCustomers();
      }
    }
  };

  // Müşteri kopyala (temel bilgileri alır, tarihler ve siparişler hariç)
  const handleCopyCustomer = async (customer) => {
    try {
      setIsLoading(true);

      // Sadece temel müşteri bilgilerini al (not, tarihler ve siparişler hariç)
      const copiedCustomerData = {
        name: customer.name + ' (Kopya)',
        phone1: customer.phone1 || '',
        phone2: customer.phone2 || '',
        email: customer.email || '',
        address: customer.address || '',
        tcNo: customer.tcNo || '',
        notes: '', // Not kopyalanmaz
        inspectionDate: '', // Keşif tarihi kopyalanmaz
        inspectionTime: '',
        orderDate: '', // Sipariş tarihi kopyalanmaz
        deliveryDate: '', // Teslimat tarihi kopyalanmaz
        deliveryTime: '',
        orderStatus: ORDER_STATUS.NEW_ORDER,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: -1, // Sipariş yok
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null // Hangi kullanıcı oluşturduğu
      };

      // Firebase'e kaydet
      const docRef = await addDoc(collection(db, 'customers'), copiedCustomerData);

      // Local state'e ekle (tüm listeyi yenilemek yerine)
      const newCustomer = {
        id: docRef.id,
        ...copiedCustomerData
      };

      setCustomers(prevCustomers => [newCustomer, ...prevCustomers]);
      setRemainingAmounts(prev => ({ ...prev, [docRef.id]: -1 }));

      showNotification(`"${customer.name}" müşterisi kopyalandı!`, 'success');
    } catch (error) {
      console.error('Müşteri kopyalanırken hata:', error);
      showNotification('Müşteri kopyalanırken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri silme onayı
  const handleDeleteConfirm = (customer) => {
    setDeleteConfirm(customer);
  };

  // Müşteri sil
  const handleDeleteCustomer = async () => {
    if (!deleteConfirm) return;

    try {
      setIsLoading(true);

      const deletedId = deleteConfirm.id;
      await deleteDoc(doc(db, 'customers', deletedId));

      setDeleteConfirm(null);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers => prevCustomers.filter(customer => customer.id !== deletedId));
      setRemainingAmounts(prev => {
        const newAmounts = { ...prev };
        delete newAmounts[deletedId];
        return newAmounts;
      });

      showNotification('Müşteri başarıyla silindi!', 'success');
    } catch (error) {
      console.error('Müşteri silinirken hata:', error);
      showNotification('Müşteri silinirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Müşteri düzenleme modunu aç
  const handleEditCustomer = (customer) => {
    // Tarihleri input için uygun formata çevir
    const customerForEdit = {
      ...customer,
      inspectionDate: formatDateForInput(customer.inspectionDate),
      orderDate: formatDateForInput(customer.orderDate),
      deliveryDate: formatDateForInput(customer.deliveryDate)
    };

    setEditingCustomer(customerForEdit);

    // Modal açıldığında body scroll'u engelle
    document.body.classList.add('modal-open');

    // Edit modu açıldığında otomatik scroll
    setTimeout(() => {
      const editSection = document.querySelector('.edit-customer-section');
      if (editSection) {
        editSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Müşteri güncelle
  const handleUpdateCustomer = async (e) => {
    if (e) e.preventDefault();

    if (!editingCustomer.name || !editingCustomer.phone1) {
      showNotification('Ad Soyad ve Telefon 1 alanları zorunludur!', 'warning');
      return;
    }

    // TC No Doğrulaması
    if (editingCustomer.tcNo && !isTCValid(editingCustomer.tcNo)) {
      showNotification('Lütfen geçerli bir TC Kimlik Numarası giriniz!', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const customerId = editingCustomer.id;

      const updatedData = {
        name: editingCustomer.name,
        phone1: editingCustomer.phone1,
        phone2: editingCustomer.phone2,
        email: editingCustomer.email,
        address: editingCustomer.address,
        notes: editingCustomer.notes,
        inspectionDate: editingCustomer.inspectionDate,
        inspectionTime: editingCustomer.inspectionTime || '',
        orderDate: editingCustomer.orderDate,
        deliveryDate: editingCustomer.deliveryDate,
        deliveryTime: editingCustomer.deliveryTime || '',
        tcNo: editingCustomer.tcNo || '',
        updatedAt: new Date()
      };

      // Keşif tarihi/saati değiştiyse (randevu ertelendi) "keşif yapıldı" işaretini kaldır
      const originalCustomer = customers.find(c => c.id === customerId);
      if (
        formatDateForInput(originalCustomer?.inspectionDate) !== formatDateForInput(editingCustomer.inspectionDate) ||
        (originalCustomer?.inspectionTime || '') !== (editingCustomer.inspectionTime || '')
      ) {
        updatedData.inspectionDone = false;
        updatedData.inspectionDoneAt = null;
        updatedData.inspectionDoneBy = null;
      }

      // Teslimat/montaj tarihi veya saati değiştiyse "montaj yapıldı" işaretini kaldır
      if (
        formatDateForInput(originalCustomer?.deliveryDate) !== formatDateForInput(editingCustomer.deliveryDate) ||
        (originalCustomer?.deliveryTime || '') !== (editingCustomer.deliveryTime || '')
      ) {
        updatedData.montajDone = false;
        updatedData.montajDoneAt = null;
        updatedData.montajDoneBy = null;
      }

      await updateDoc(doc(db, 'customers', customerId), updatedData);

      // Local state'i güncelle (tüm listeyi yeniden çekmek yerine)
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? { ...customer, ...updatedData }
            : customer
        )
      );

      // Kalan ödemeyi hesapla ve güncelle
      const remaining = await calculateRemainingAmount(editingCustomer);
      setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));

      // Firebase'e remainingAmount kaydet
      await updateDoc(doc(db, 'customers', customerId), {
        remainingAmount: remaining
      });

      setEditingCustomer(null);
      // Modal kapandığında body scroll'u geri aç
      document.body.classList.remove('modal-open');

      showNotification('Müşteri başarıyla güncellendi!', 'success');
    } catch (error) {
      console.error('Müşteri güncellenirken hata:', error);
      showNotification('Müşteri güncellenirken bir hata oluştu: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="customer-list-container cl">
      {/* Bildirim */}
      {notification && (
        <div className={`cl-toast cl-toast-${notification.type}`} role="status">
          <span className="cl-toast-icon">
            {notification.type === 'success' ? <CheckCircleIcon size={18} /> : <AlertCircleIcon size={18} />}
          </span>
          <span className="cl-toast-message">{notification.message}</span>
          <button type="button" className="cl-toast-close" onClick={() => setNotification(null)} aria-label="Kapat">
            <XIcon size={16} />
          </button>
          <span className="cl-toast-progress" aria-hidden="true" />
        </div>
      )}

      <header className="cl-header">
        <div className="cl-header-text">
          <h2 className="cl-title">{isMontajci ? 'Montaj Listesi' : 'Müşteriler'}</h2>
          <p className="cl-subtitle">
            {customers.length} müşteri
            {!isMontajci && pendingPaymentCount > 0 && <> · <span className="cl-subtitle-due">{pendingPaymentCount} ödeme bekliyor</span></>}
          </p>
        </div>
        {permissions?.canCreateCustomer && (
          <button
            type="button"
            onClick={() => {
              setIsAdding(!isAdding);
              setCurrentStep('customer');
            }}
            className={`cl-btn ${isAdding ? '' : 'cl-btn-primary'}`}
            disabled={isLoading}
          >
            {isAdding ? <><XIcon size={16} /> Vazgeç</> : <><PlusIcon size={16} strokeWidth={2.2} /> Yeni müşteri</>}
          </button>
        )}
      </header>

      {/* Arama, durum filtresi, görünüm */}
      {!isAdding && !editingCustomer && (
        <div className="cl-toolbar">
          <label className="cl-search">
            <SearchIcon size={17} />
            <input
              type="search"
              placeholder="Ad, telefon veya adres ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Müşteri ara"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} aria-label="Aramayı temizle">
                <XIcon size={15} />
              </button>
            )}
          </label>

          {!isMontajci && (
            <div className="cl-filters" role="group" aria-label="Sipariş durumu">
              <button
                type="button"
                className={statusFilter === '' ? 'is-active' : ''}
                aria-pressed={statusFilter === ''}
                onClick={() => setStatusFilter('')}
              >
                Tümü <span>{statusCounts.all}</span>
              </button>
              {Object.entries(ORDER_STATUS_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={statusFilter === key ? 'is-active' : ''}
                  aria-pressed={statusFilter === key}
                  onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
                >
                  <i className={`cl-dot cl-tone-${STATUS_TONES[key]}`} aria-hidden="true" />
                  {label} <span>{statusCounts[key] || 0}</span>
                </button>
              ))}
            </div>
          )}

          <div className="cl-toolbar-end">
            <label className="cl-select">
              <span>Sayfa başına</span>
              <select value={itemsPerPage} onChange={(e) => handleItemsPerPageChange(e.target.value)}>
                {PAGINATION_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
            {!isMobile && (
              <div className="cl-view" role="group" aria-label="Görünüm">
                <button
                  type="button"
                  className={viewMode === 'table' ? 'is-active' : ''}
                  aria-pressed={viewMode === 'table'}
                  onClick={() => setViewMode('table')}
                  aria-label="Liste görünümü"
                  title="Liste"
                >
                  <ListIcon size={17} />
                </button>
                <button
                  type="button"
                  className={viewMode === 'card' ? 'is-active' : ''}
                  aria-pressed={viewMode === 'card'}
                  onClick={() => setViewMode('card')}
                  aria-label="Kart görünümü"
                  title="Kart"
                >
                  <GridIcon size={17} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isAdding && (
        <div className="add-customer-form">
          <h3>Yeni Müşteri Ekle</h3>

          {/* Aşama Göstergesi */}
          <div className="step-indicator">
            <div
              className={`step ${currentStep === 'customer' ? 'active' : 'completed'}`}
              onClick={() => setCurrentStep('customer')}
              style={{ cursor: 'pointer' }}
            >
              <span className="step-number">1</span>
              <span className="step-text">Müşteri Bilgileri</span>
            </div>
            <div
              className={`step ${currentStep === 'order' ? 'active' : ''}`}
              onClick={() => {
                if (newCustomer.id) { // Müşteri kaydedilmişse tıklanabilir
                  setCurrentStep('order');
                }
              }}
              style={{ cursor: newCustomer.id ? 'pointer' : 'not-allowed' }}
            >
              <span className="step-number">2</span>
              <span className="step-text">Sipariş Detayları</span>
            </div>
          </div>

          {/* Üst Kısım - Müşteri Bilgileri */}
          <div className={`customer-info-section ${currentStep === 'customer' ? 'active' : 'hidden'}`}>
            <h4>Müşteri Bilgileri</h4>
            <form onSubmit={handleAddCustomer}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>TC No / Vergi No</label>
                  <input
                    type="text"
                    value={newCustomer.tcNo}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setNewCustomer({ ...newCustomer, tcNo: value });
                    }}
                    placeholder="TC veya Vergi No"
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Telefon 1 *</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(newCustomer.phone1.replace(/^90/, ''))}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        const digits = formatted.replace(/\D/g, '');
                        setNewCustomer({ ...newCustomer, phone1: digits ? '90' + digits : '' });
                      }}
                      placeholder="5XX XXX XX XX"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Telefon 2</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(newCustomer.phone2.replace(/^90/, ''))}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        const digits = formatted.replace(/\D/g, '');
                        setNewCustomer({ ...newCustomer, phone2: digits ? '90' + digits : '' });
                      }}
                      placeholder="5XX XXX XX XX"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Adres</label>
                <textarea
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  rows="3"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group full-width">
                <label>Not</label>
                <textarea
                  value={newCustomer.notes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                  rows="3"
                  disabled={isLoading}
                />
              </div>

              <div className="form-grid date-grid">
                <div className="form-group">
                  <label>Keşif Tarihi ve Saati</label>
                  <div className="date-time-inputs">
                    <input
                      type="date"
                      value={newCustomer.inspectionDate}
                      onChange={(e) => setNewCustomer({ ...newCustomer, inspectionDate: e.target.value })}
                      disabled={isLoading}
                    />
                    <input
                      type="time"
                      value={newCustomer.inspectionTime}
                      onChange={(e) => setNewCustomer({ ...newCustomer, inspectionTime: e.target.value })}
                      disabled={isLoading}
                      aria-label="Keşif Saati"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Sipariş Tarihi</label>
                  <input
                    type="date"
                    value={newCustomer.orderDate}
                    onChange={(e) => setNewCustomer({ ...newCustomer, orderDate: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Teslimat / Montaj Tarihi ve Saati</label>
                  <div className="date-time-inputs">
                    <input
                      type="date"
                      value={newCustomer.deliveryDate}
                      onChange={(e) => setNewCustomer({ ...newCustomer, deliveryDate: e.target.value })}
                      disabled={isLoading}
                    />
                    <input
                      type="time"
                      value={newCustomer.deliveryTime}
                      onChange={(e) => setNewCustomer({ ...newCustomer, deliveryTime: e.target.value })}
                      disabled={isLoading}
                      aria-label="Teslimat / Montaj Saati"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`save-btn ${(isLoading || isSubmittingRef.current) ? 'loading' : ''}`}
                  disabled={isLoading || isSubmittingRef.current}
                  style={{
                    pointerEvents: (isLoading || isSubmittingRef.current) ? 'none' : 'auto',
                    userSelect: 'none',
                    opacity: (isLoading || isSubmittingRef.current) ? 0.6 : 1
                  }}
                >
                  {(isLoading || isSubmittingRef.current) ? 'Kaydediliyor...' : 'Müşteriyi Kaydet'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAdding(false);
                    setCurrentStep('customer');
                  }}
                  className="cancel-btn"
                  disabled={isLoading}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>

          {/* Alt Kısım - Sipariş Detayları */}
          <div className={`order-details-section ${currentStep === 'order' ? 'active' : 'hidden'}`}>
            <OrderDetails
              customerId={newCustomer.id || 'temp_' + Date.now()}
              customerName={newCustomer.name}
              customerInfo={newCustomer}
              isLoading={isLoading}
              orderStatus={newCustomer.orderStatus}
              onOrderStatusChange={handleNewCustomerOrderStatusChange}
              onVadeliYuzdeChange={handleNewCustomerVadeliYuzdeChange}
              onGenelIskontoYuzdeChange={handleNewCustomerGenelIskontoYuzdeChange}
              isEditMode={false}
              permissions={permissions}
              onCustomerSaved={(paymentInfo) => {
                // Ödeme bilgileri değiştiğinde newCustomer state'ini güncelle
                if (paymentInfo && typeof paymentInfo === 'object' && 'paidAmount' in paymentInfo) {
                  setNewCustomer(prev => ({
                    ...prev,
                    paidAmount: paymentInfo.paidAmount,
                    isPaymentCompleted: paymentInfo.isPaymentCompleted,
                    remainingAmount: paymentInfo.remainingAmount
                  }));
                }
              }}
            />

            {/* İşlemi Tamamla butonu sipariş detaylarının altında */}
            <div className="complete-form-actions">
              <button
                type="button"
                onClick={handleCompleteCustomer}
                className="complete-btn"
                disabled={isLoading}
              >
                İşlemi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCustomer && <div className="cl-backdrop" aria-hidden="true" />}
      {editingCustomer && (
        <div className="edit-customer-section" role="dialog" aria-modal="true" aria-label="Müşteri düzenle">
          <div className="edit-customer-header">
            <div className="header-left">
              <h3>Müşteri Düzenle</h3>
              <span className="auto-save-info">
                <InfoIcon size={14} />
                Siparişler otomatik kaydedilir
              </span>
            </div>
            <button
              onClick={() => {
                // Düzenleme modundan çık
                const customerId = editingCustomer.id;
                setEditingCustomer(null);
                document.body.classList.remove('modal-open');

                // Sadece bu müşterinin remainingAmount'unu güncelle (tüm listeyi yenileme)
                (async () => {
                  try {
                    const customer = customers.find(c => c.id === customerId);
                    if (customer) {
                      const remaining = await calculateRemainingAmount(customer);
                      setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));

                      // Firebase'e de kaydet
                      await updateDoc(doc(db, 'customers', customerId), {
                        remainingAmount: remaining,
                        updatedAt: new Date()
                      });
                    }
                  } catch (error) {
                    console.warn('Kalan ödeme güncellenirken hata:', error);
                  }
                })();
              }}
              className="close-edit-btn"
              title="Kapat"
              aria-label="Kapat"
            >
              <XIcon size={18} />
            </button>
          </div>

          <div className="edit-customer-content">
            {/* Müşteri Bilgileri Bölümü */}
            <div className="customer-info-section">
              <div
                className="section-header collapsible"
                onClick={() => setIsCustomerInfoCollapsed(!isCustomerInfoCollapsed)}
              >
                <h4>Müşteri Bilgileri</h4>
                <ChevronDownIcon size={18} className={`cl-collapse-chevron${isCustomerInfoCollapsed ? '' : ' is-open'}`} />
              </div>

              <div className={`section-content ${isCustomerInfoCollapsed ? 'collapsed' : ''}`}>
                <form onSubmit={handleUpdateCustomer}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Ad Soyad *</label>
                      <input
                        type="text"
                        value={editingCustomer.name}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>TC No / Vergi No</label>
                      <input
                        type="text"
                        value={editingCustomer.tcNo || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                          setEditingCustomer({ ...editingCustomer, tcNo: value });
                        }}
                        placeholder="TC veya Vergi No"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label>Telefon 1 *</label>
                      <div className="phone-input-container">
                        <span className="phone-prefix">+90</span>
                        <input
                          type="tel"
                          className="phone-main-input"
                          value={formatPhoneNumber((editingCustomer.phone1 || '').replace(/^90/, ''))}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            const digits = formatted.replace(/\D/g, '');
                            setEditingCustomer({ ...editingCustomer, phone1: digits ? '90' + digits : '' });
                          }}
                          placeholder="5XX XXX XX XX"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Telefon 2</label>
                      <div className="phone-input-container">
                        <span className="phone-prefix">+90</span>
                        <input
                          type="tel"
                          className="phone-main-input"
                          value={formatPhoneNumber((editingCustomer.phone2 || '').replace(/^90/, ''))}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            const digits = formatted.replace(/\D/g, '');
                            setEditingCustomer({ ...editingCustomer, phone2: digits ? '90' + digits : '' });
                          }}
                          placeholder="5XX XXX XX XX"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={editingCustomer.email}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group full-width" style={{ marginTop: '10px' }}>
                    <label>Adres</label>
                    <textarea
                      value={editingCustomer.address}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Not</label>
                    <textarea
                      value={editingCustomer.notes}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, notes: e.target.value })}
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-grid" style={{ marginTop: '10px' }}>
                    <div className="form-group">
                      <label>Keşif Tarihi ve Saati</label>
                      <div className="date-time-inputs">
                        <input
                          type="date"
                          value={formatDateForInput(editingCustomer.inspectionDate)}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, inspectionDate: e.target.value })}
                          disabled={isLoading}
                        />
                        <input
                          type="time"
                          value={editingCustomer.inspectionTime || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, inspectionTime: e.target.value })}
                          disabled={isLoading}
                          aria-label="Keşif Saati"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Sipariş Tarihi</label>
                      <input
                        type="date"
                        value={formatDateForInput(editingCustomer.orderDate)}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, orderDate: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Teslimat / Montaj Tarihi ve Saati</label>
                      <div className="date-time-inputs">
                        <input
                          type="date"
                          value={formatDateForInput(editingCustomer.deliveryDate)}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, deliveryDate: e.target.value })}
                          disabled={isLoading}
                        />
                        <input
                          type="time"
                          value={editingCustomer.deliveryTime || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, deliveryTime: e.target.value })}
                          disabled={isLoading}
                          aria-label="Teslimat / Montaj Saati"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="save-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Güncelleniyor...' : 'Güncelle'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const customerId = editingCustomer.id;
                        setEditingCustomer(null);
                        document.body.classList.remove('modal-open');
                        (async () => {
                          try {
                            const customer = customers.find(c => c.id === customerId);
                            if (customer) {
                              const remaining = await calculateRemainingAmount(customer);
                              setRemainingAmounts(prev => ({ ...prev, [customerId]: remaining }));
                              await updateDoc(doc(db, 'customers', customerId), {
                                remainingAmount: remaining,
                                updatedAt: new Date()
                              });
                            }
                          } catch (error) {
                            console.warn('Kalan ödeme güncellenirken hata:', error);
                          }
                        })();
                      }}
                      className="cancel-btn"
                      disabled={isLoading}
                    >
                      Kapat
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Sipariş Detayları Bölümü */}
            <div className="order-details-section">
              <OrderDetails
                customerId={editingCustomer.id}
                customerName={editingCustomer.name}
                customerInfo={editingCustomer}
                isLoading={isLoading}
                orderStatus={editingCustomer.orderStatus}
                onOrderStatusChange={(status) => handleOrderStatusChange(editingCustomer.id, status)}
                isEditMode={true}
                permissions={permissions}
                onSaveVadeliSuccess={handleEditCustomerVadeliSaveSuccess}
                onCustomerSaved={handleCustomerPaymentUpdate}
              />
            </div>
          </div>
        </div>
      )}

      {!isAdding && !editingCustomer && (
        <section className="cl-list" aria-busy={isLoading}>
          {isLoading && currentCustomers.length === 0 ? (
            <div className="cl-loading">
              {[0, 1, 2, 3].map(i => <span key={i} className="cl-skeleton cl-skeleton-row" />)}
            </div>
          ) : currentCustomers.length === 0 ? (
            <div className="cl-empty">
              <span className="cl-empty-icon"><UsersIcon size={24} /></span>
              <strong>{searchTerm || statusFilter ? 'Eşleşen müşteri yok' : 'Henüz müşteri eklenmemiş'}</strong>
              <span>
                {searchTerm || statusFilter
                  ? 'Aramayı veya durum filtresini değiştirmeyi deneyin.'
                  : 'Yeni müşteri butonuyla ilk kaydı oluşturun.'}
              </span>
              {(searchTerm || statusFilter) && (
                <button type="button" className="cl-btn" onClick={() => { setSearchTerm(''); setStatusFilter(''); }}>
                  Filtreleri temizle
                </button>
              )}
            </div>
          ) : effectiveView === 'table' ? (
            <div className="cl-table-wrap">
              <table className="cl-table">
                <thead>
                  <tr>
                    <th scope="col">Müşteri</th>
                    <th scope="col">Sipariş</th>
                    <th scope="col">Teslimat / Montaj</th>
                    <th scope="col">Durum</th>
                    <th scope="col">Adres</th>
                    {!isMontajci && <th scope="col" className="cl-num">Kalan ödeme</th>}
                    <th scope="col"><span className="cl-sr">İşlemler</span></th>
                  </tr>
                </thead>
                <tbody>
                  {currentCustomers.map((customer) => (
                    <tr key={customer.id}>
                      <td>
                        <div className="cl-person">
                          <span className="cl-avatar" aria-hidden="true">{getInitials(customer.name)}</span>
                          <span className="cl-person-text">
                            <button type="button" className="cl-person-name" onClick={() => handleEditCustomer(customer)} disabled={isLoading}>
                              {customer.name || 'İsimsiz müşteri'}
                            </button>
                            {customer.phone1 && (
                              <a className="cl-person-phone" href={`tel:+${customer.phone1.replace(/\D/g, '')}`}>
                                {displayPhone(customer.phone1)}
                              </a>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="cl-date">{formatDate(customer.orderDate)}</td>
                      <td className="cl-date">
                        {formatDate(customer.deliveryDate)}
                        {customer.deliveryTime && <span className="cl-time">{customer.deliveryTime}</span>}
                      </td>
                      <td><StatusChip status={customer.orderStatus} /></td>
                      <td><span className="cl-address" title={customer.address}>{customer.address || '—'}</span></td>
                      {!isMontajci && (
                        <td className="cl-num">
                          <PaymentChip amount={remainingAmounts[customer.id]} completed={customer.isPaymentCompleted} />
                        </td>
                      )}
                      <td>
                        <div className="cl-actions">
                          <button
                            type="button"
                            onClick={() => handleEditCustomer(customer)}
                            className="cl-icon-btn"
                            disabled={isLoading}
                            aria-label={permissions?.canEditCustomer ? `${customer.name} düzenle` : `${customer.name} detaylar`}
                            title={permissions?.canEditCustomer ? 'Düzenle' : 'Detaylar'}
                          >
                            {permissions?.canEditCustomer ? <PencilIcon size={16} /> : <EyeIcon size={16} />}
                          </button>
                          {permissions?.canCreateCustomer && (
                            <button
                              type="button"
                              onClick={() => handleCopyCustomer(customer)}
                              className="cl-icon-btn"
                              disabled={isLoading}
                              aria-label={`${customer.name} kopyala`}
                              title="Müşteriyi kopyala"
                            >
                              <CopyIcon size={16} />
                            </button>
                          )}
                          {permissions?.canDeleteCustomer && (
                            <button
                              type="button"
                              onClick={() => handleDeleteConfirm(customer)}
                              className="cl-icon-btn cl-icon-danger"
                              disabled={isLoading}
                              aria-label={`${customer.name} sil`}
                              title="Sil"
                            >
                              <TrashIcon size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="cl-grid">
              {currentCustomers.map((customer) => (
                <CustomerCard
                  key={customer.id}
                  customer={customer}
                  onEdit={handleEditCustomer}
                  onCopy={handleCopyCustomer}
                  onDelete={handleDeleteConfirm}
                  isLoading={isLoading}
                  formatDate={formatDate}
                  remainingAmount={remainingAmounts[customer.id]}
                  showPayment={!isMontajci}
                  permissions={permissions}
                />
              ))}
            </div>
          )}
          {isLoading && currentCustomers.length > 0 && <div className="cl-refreshing" aria-hidden="true" />}
        </section>
      )}

      {/* Sayfalama */}
      {!isAdding && !editingCustomer && filteredCustomers.length > 0 && (
        <nav className="cl-pagination" aria-label="Sayfalar">
          <span className="cl-pagination-info">
            {startIndex + 1}–{Math.min(endIndex, filteredCustomers.length)} / {filteredCustomers.length} müşteri
          </span>
          {totalPages > 1 && (
            <div className="cl-pages">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Önceki sayfa"
              >
                <ChevronLeftIcon size={17} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => handlePageChange(page)}
                    className={currentPage === page ? 'is-active' : ''}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Sonraki sayfa"
              >
                <ChevronRightIcon size={17} />
              </button>
            </div>
          )}
        </nav>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <div className="cl-modal-backdrop" onClick={() => !isLoading && setDeleteConfirm(null)}>
          <div className="cl-modal" role="alertdialog" aria-modal="true" aria-labelledby="cl-delete-title" onClick={e => e.stopPropagation()}>
            <span className="cl-modal-icon"><TrashIcon size={22} /></span>
            <h3 id="cl-delete-title">Müşteri silinsin mi?</h3>
            <p>
              <strong>{deleteConfirm.name}</strong> ve siparişleri kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </p>
            <div className="cl-modal-actions">
              <button type="button" onClick={() => setDeleteConfirm(null)} className="cl-btn" disabled={isLoading}>
                Vazgeç
              </button>
              <button type="button" onClick={handleDeleteCustomer} className="cl-btn cl-btn-danger" disabled={isLoading}>
                {isLoading ? 'Siliniyor…' : 'Evet, sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList; 